import React, { useState } from 'react';
import styled from 'styled-components';
import BWAccordion from 'components/shared/BWAccordion';
import FormInput from 'components/shared/BWFormControl/FormInput';
import DataTable from 'components/shared/DataTable';
import PropTypes from 'prop-types';

import { useFormContext, useFieldArray } from 'react-hook-form';
import { Segmented } from 'antd';
import FormNumber from 'components/shared/BWFormControl/FormNumber';
import { TYPE_PROMOTION_VALUE } from 'pages/Coupon/utils/constants';
import { typePromotionValues } from 'pages/Coupon/utils/helpers';
import { getValueWatchByIndex, checkAndGetValuePromational } from './mathCoupon';
import PromotionProductModal from 'pages/PromotionOffers/components/add/PromotionProductModal';
import CouponAutoCodeGift from './CouponAutoCodeGift';
import { formatIntegerInput, parseIntegerInput } from 'pages/Coupon/utils/numberFormat';

const SegmentedStyled = styled(Segmented)`
  .ant-segmented-item-selected {
    background-color: #1b3c40 !important;
    color: white !important;
  }
`;

const AutoCouponGuide = styled.div`
  margin: 0 0 12px;
  padding: 11px 14px;
  border: 1px solid #b9dcff;
  border-radius: 10px;
  background: #f0f7ff;
  color: #344054;
  font-size: 13px;
  line-height: 1.5;

  strong {
    color: #101828;
  }
`;

const FieldWithUnit = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  > *:first-child {
    min-width: 0;
    flex: 1;
  }
`;

const RangeFields = styled.div`
  display: grid;
  grid-template-columns: minmax(96px, 1fr) 16px minmax(96px, 1fr);
  gap: 6px;
  align-items: center;

  .bw_coupon_range_separator {
    color: #98a2b3;
    font-weight: 700;
    text-align: center;
  }
`;

const CouponValue = ({ loading, title, disabled }) => {
  const methods = useFormContext();
  const { setValue, watch, unregister } = methods;
  const [modalCoupon, setModalCoupon] = useState(undefined);
  const { fields } = useFieldArray({
    control: methods.control,
    name: 'promotional_list',
  });

  const columns = [
    {
      header: 'Mức giảm',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '210px' },
      style: { minWidth: '210px' },
      formatter: (_, index) => {
        const codeType = Number(methods.watch(`promotional_list.${index}.code_type`));
        return (
          <FieldWithUnit>
            <FormNumber
              bordered={true}
              min={0}
              max={codeType === TYPE_PROMOTION_VALUE.PERCENT ? 100 : undefined}
              precision={0}
              formatter={codeType === TYPE_PROMOTION_VALUE.MONEY ? formatIntegerInput : undefined}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.code_value`}
              onChange={(e) => {
                checkAndGetValuePromational(methods, null, index, 'code_value', e);
              }}
              validation={{
                required: 'Giá trị khuyến mại là bắt buộc',
              }}
              disabled={disabled}
            />
            <SegmentedStyled
              disabled={disabled}
              value={methods.watch(`promotional_list.${index}.code_type`)}
              options={typePromotionValues}
              onChange={(e) => {
                checkAndGetValuePromational(methods, null, index, 'code_type', e);
              }}
            />
          </FieldWithUnit>
        );
      },
    },
    {
      header: 'Số mã cần tạo',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '150px' },
      style: { minWidth: '150px' },
      formatter: (_, index) => {
        return (
          <React.Fragment>
            <FormNumber
              bordered={true}
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.quantity`}
              validation={{
                required: 'Số mã cần tạo là bắt buộc',
              }}
              onChange={(e) => {
                checkAndGetValuePromational(methods, null, index, 'quantity', e);
              }}
              disabled={disabled}
            />
          </React.Fragment>
        );
      },
    },
    {
      header: 'Giá trị đơn hàng áp dụng',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '260px' },
      style: { minWidth: '260px' },
      formatter: (_, index) => {
        return (
          <RangeFields>
            <FormNumber
              disabled={disabled}
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.min_total_money`}
              placeholder='Từ'
            />
            <div className='bw_coupon_range_separator'>→</div>
            <FormNumber
              placeholder='Đến'
              disabled={disabled}
              type='text'
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.max_total_money`}
            />
          </RangeFields>
        );
      },
    },
    {
      header: 'Số sản phẩm áp dụng',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '230px' },
      style: { minWidth: '230px' },
      formatter: (_, index) => {
        return (
          <RangeFields>
            <FormNumber
              disabled={disabled}
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.min_count`}
              placeholder='Từ'
            />
            <div className='bw_coupon_range_separator'>→</div>
            <FormNumber
              placeholder='Đến'
              disabled={disabled}
              type='text'
              min={0}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              field={`promotional_list.${index}.max_count`}
            />
          </RangeFields>
        );
      },
    },
    {
      header: 'Giảm tối đa',
      classNameHeader: 'bw_text_center',
      styleHeader: { minWidth: '160px' },
      style: { minWidth: '160px' },
      hidden: !(methods.watch('promotional_list') ?? []).some(
        (item) => Number(item?.code_type) === TYPE_PROMOTION_VALUE.PERCENT,
      ),
      formatter: (_, index) => {
        const maxValue = getValueWatchByIndex(methods, index, 'total_value', 'promotional_list');
        return (
          getValueWatchByIndex(methods, index, 'code_type', 'promotional_list') * 1 ===
            TYPE_PROMOTION_VALUE.PERCENT && (
            <FormNumber
              type='text'
              style={{
                border: '1px solid #ccc',
                borderRadius: '6px',
              }}
              min={0}
              max={maxValue}
              precision={0}
              formatter={formatIntegerInput}
              parser={parseIntegerInput}
              disabled={disabled || methods.watch(`promotional_list.${index}.code_type`) === TYPE_PROMOTION_VALUE.MONEY}
              field={`promotional_list.${index}.max_value_reduce`}
              placeholder='Giá trị giảm tối đa'
            />
          )
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <BWAccordion isRequired title={title}>
        <div className='bw_col_12'>
          <div className='bw_row'>
            <div className='bw_col_12'>
              <div className='bw_col_4'>
                <label className='bw_checkbox bw_col_12'>
                  <FormInput
                    disabled={disabled}
                    type='checkbox'
                    field='is_gift_code'
                    onChange={() => {
                      const is_gift_code = watch('is_gift_code');
                      methods.setValue('is_gift_code', is_gift_code ? 0 : 1);
                      if (!is_gift_code) {
                        unregister('promotional_list');
                      } else {
                        setValue('promotional_list', [
                          {
                            code_value: undefined,
                            code_type: 1,
                            quantity: undefined,
                            min_total_money: undefined,
                            max_total_money: undefined,
                            min_count: undefined,
                            max_count: undefined,
                            max_value_reduce: undefined,
                          },
                        ]);
                      }
                    }}
                  />
                  <span />
                  Mã giảm giá quà tặng
                </label>
              </div>
            </div>
          </div>
        </div>
        {!Boolean(watch('is_gift_code')) ? (
          <React.Fragment>
            <AutoCouponGuide>
              <strong>Chế độ tự sinh mã:</strong> nhập <strong>Số mã cần tạo</strong>, hệ thống sẽ tạo đúng số lượng mã
              riêng biệt theo cấu hình ký tự, tiền tố và hậu tố phía trên.
            </AutoCouponGuide>
            <DataTable
              style={{
                marginTop: '0px',
              }}
              hiddenActionRow
              noPaging
              noSelect
              data={fields}
              columns={columns}
              loading={loading}
            />
          </React.Fragment>
        ) : (
          <div className='bw_col_12'>
            <React.Fragment>
              <CouponAutoCodeGift
                disabled={disabled}
                hiddenAction={{ detail: true }}
                contentCreate='Chọn sản phẩm'
                noLoadData
                handleOpenModalGift={() => setModalCoupon(true)}
                fieldProduct='auto_code_gift_list'
              />
              {modalCoupon && (
                <PromotionProductModal
                  fieldProduct='auto_code_gift_list'
                  title='Chọn sản phẩm quà tặng'
                  onClose={() => setModalCoupon(false)}
                />
              )}
            </React.Fragment>
          </div>
        )}
      </BWAccordion>
    </React.Fragment>
  );
};

CouponValue.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default CouponValue;
